import euFlag from '@/assets/images/flag-eu.svg';
import { useData } from '@/contexts/data-context/use-data';
import { Button } from '@/design-system/button/button';
import { Stack } from '@/design-system/stack/stack';
import { exhaustiveCheck } from '@/utils/exhaustive-check';
import { postSwrFetcher } from '@/utils/post-swr-fetcher';
import { CompanyRequestType } from '@seapoint/server/enums';
import { useLocation } from 'react-router';
import { toast } from 'sonner';
import useSWRMutation from 'swr/mutation';

const REGISTER_INTEREST_URL = '/api/treasury/register-interest';

type RegisterInterestRequest = {
  source: string;
};

type EuWaitListProps = {
  hasRecordedInterest: boolean;
};

export const EuWaitList = ({ hasRecordedInterest }: EuWaitListProps) => {
  const location = useLocation();
  const { data, mutations } = useData();

  const { trigger: triggerJoinWaitList, isMutating: isJoiningWaitList } = useSWRMutation<
    { success: boolean },
    Error,
    string,
    RegisterInterestRequest
  >(REGISTER_INTEREST_URL, postSwrFetcher);

  const { title, description } = getEuWaitListContent(hasRecordedInterest);

  return (
    <Stack space={6} align="center">
      <Stack space={4} align="center">
        <div className="h-10 w-10 overflow-clip rounded-full">
          <img src={euFlag} alt="" className="h-full w-full" />
        </div>

        <Stack space={2} align="center">
          <h2 className="text-center text-lg font-medium text-foreground">{title}</h2>

          <p className="text-center text-sm font-normal text-muted-foreground">{description}</p>
        </Stack>
      </Stack>

      {!hasRecordedInterest && (
        <Button
          variant="secondary"
          isLoading={isJoiningWaitList}
          onClick={async () => {
            try {
              await triggerJoinWaitList({ source: location.pathname });
              // Update the company's requests in cache to avoid full page reload
              const company = data?.companies[0];
              if (company && data) {
                await mutations.mutateCache?.({
                  companies: data.companies.map((c) =>
                    c.id === company.id
                      ? {
                          ...c,
                          requests: [
                            ...(c.requests ?? []),
                            { type: CompanyRequestType.NON_GB_TREASURY } as (typeof c.requests)[number],
                          ],
                        }
                      : c,
                  ),
                });
              }
            } catch {
              toast.error('Failed to register interest. Please try again.');
            }
          }}
        >
          Register interest
        </Button>
      )}
    </Stack>
  );
};

const getEuWaitListContent = (hasRecordedInterest: boolean) => {
  switch (hasRecordedInterest) {
    case true:
      return {
        title: 'Thanks for registering your interest.',
        description: 'A member of the team will contact you when Treasury is available for EU businesses.',
      };
    case false:
      return {
        title: 'Treasury is not available for EU businesses yet',
        description:
          "Treasury accounts are only available for UK-based businesses. If you're based in the EU, you can register your interest.",
      };
    default:
      return exhaustiveCheck(hasRecordedInterest);
  }
};
